import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useOrdersData } from './useOrdersData';
import { useOrderJobsData } from './useOrderJobsData';
import { useResultsData } from './useResultsData';
import { useModalAccessibility } from './useAccessibility';
import { useModalPerformanceMonitoring } from './usePerformanceMonitoring';

/**
 * Unified Modal Data Management Hook
 * 
 * Provides comprehensive data management for modal components by:
 * - Coordinating orders, jobs, and results data
 * - Managing loading states and errors
 * - Providing unified statistics and insights
 * - Handling accessibility and performance monitoring
 * - Offering simplified API for complex data operations
 */

export const useOrdersWithJobsData = ({
  // Configuration
  filters = {},
  pageSize = 20,
  modalId = 'modal-data',
  accordionMode = true,
  
  // Feature flags
  enableAccessibility = true,
  enablePerformanceMonitoring = true,
  enableRealTimeJobs = true,
  enableResultsParsing = true,
  
  // User scope configuration
  userScope = 'all', // 'all' | 'current_user' | 'other_users'
  
  // Callbacks
  onDataLoaded = null,
  onError = null
} = {}) => {

  // Individual data hooks
  const ordersData = useOrdersData({ filters, pageSize, modalId, userScope });
  
  // Memoize orderIds to prevent infinite re-renders
  const orderIds = useMemo(() => 
    ordersData.orders.map(o => o.id),
    [ordersData.orders]
  );

  const jobsData = useOrderJobsData({
    orderIds,
    autoSubscribe: enableRealTimeJobs,
    groupByProvider: true,
    userScope
  });
  
  const resultsData = useResultsData({
    jobIds: jobsData.jobs.map(j => j.id),
    autoLoad: true,
    parseContent: enableResultsParsing
  });

  // Accessibility (optional)
  const accessibility = useModalAccessibility(
    true, // Always ready for modal usage
    modalId
  );

  // Performance monitoring (optional)
  const performance = useModalPerformanceMonitoring(
    modalId,
    true
  );

  // Unified state
  const [state, setState] = useState({
    expandedOrders: {},
    selectedOrder: null,
    selectedJob: null,
    viewMode: 'list', // 'list', 'order', 'job'
    filterState: {},
    lastAction: null
  });

  // Unified loading state
  const isLoading = useMemo(() => {
    return ordersData.loading || jobsData.loading || resultsData.loading;
  }, [ordersData.loading, jobsData.loading, resultsData.loading]);

  // Unified error state
  const error = useMemo(() => {
    return ordersData.error || jobsData.error || resultsData.error;
  }, [ordersData.error, jobsData.error, resultsData.error]);

  // Enhanced orders with job and result counts
  const enhancedOrders = useMemo(() => {
    return ordersData.orders.map(order => {
      const jobs = jobsData.jobsByOrderId[order.id] || [];
      const completedJobs = jobs.filter(j => j.status === 'completed');
      const jobsWithResults = jobs.filter(j => resultsData.pooledResults[j.id]);
      
      return {
        ...order,
        jobCount: jobs.length,
        completedJobCount: completedJobs.length,
        resultsCount: jobsWithResults.length,
        progress: jobs.length > 0 ? completedJobs.length / jobs.length : 0,
        hasResults: jobsWithResults.length > 0,
        jobs,
        totalCost: jobs.reduce((sum, job) => sum + (job.cost || 0), 0)
      };
    });
  }, [ordersData.orders, jobsData.jobsByOrderId, resultsData.pooledResults]);

  // Unified statistics
  const unifiedStats = useMemo(() => {
    return {
      orders: {
        ...ordersData.stats,
        withJobs: enhancedOrders.filter(o => o.jobCount > 0).length,
        withResults: enhancedOrders.filter(o => o.hasResults).length
      },
      jobs: jobsData.stats,
      results: resultsData.stats,
      overall: {
        totalItems: ordersData.stats.total + jobsData.stats.total + resultsData.stats.totalResults,
        completionRate: jobsData.stats.total > 0 ? 
          jobsData.stats.byStatus.completed / jobsData.stats.total : 0,
        resultsRate: jobsData.stats.total > 0 ? 
          resultsData.stats.totalResults / jobsData.stats.total : 0,
        totalCost: ordersData.stats.totalCost + jobsData.stats.totalCost,
        dataQuality: resultsData.stats.avgConfidence || 0
      }
    };
  }, [ordersData.stats, jobsData.stats, resultsData.stats, enhancedOrders]);

  // Order management
  const toggleOrderExpansion = useCallback((orderId) => {
    setState(prev => ({
      ...prev,
      expandedOrders: accordionMode 
        ? { [orderId]: !prev.expandedOrders[orderId] }  // Accordion: only one open at a time
        : { ...prev.expandedOrders, [orderId]: !prev.expandedOrders[orderId] },  // Multi: keep others open
      lastAction: { type: 'toggle_order', orderId, timestamp: Date.now() }
    }));
    
    // Ensure jobs are loaded for this order
    if (!jobsData.jobsByOrderId[orderId]) {
      jobsData.subscribe([orderId]);
    }

    // Announce to screen readers
    if (enableAccessibility) {
      const isExpanded = !state.expandedOrders[orderId];
      const order = ordersData.getOrderById(orderId);
      accessibility.announce(
        `Order ${order?.orderName || orderId} ${isExpanded ? 'expanded' : 'collapsed'}`,
        'polite'
      );
    }
  }, [accordionMode, state.expandedOrders, jobsData, ordersData, accessibility, enableAccessibility]);

  const selectOrder = useCallback((orderId) => {
    setState(prev => ({
      ...prev,
      selectedOrder: orderId,
      selectedJob: null,
      viewMode: 'order',
      lastAction: { type: 'select_order', orderId, timestamp: Date.now() }
    }));

    if (enablePerformanceMonitoring) {
      performance.trackModalAction('order_selected', { orderId });
    }
  }, [performance, enablePerformanceMonitoring]);

  const selectJob = useCallback((jobId, orderId = null) => {
    setState(prev => ({
      ...prev,
      selectedJob: jobId,
      selectedOrder: orderId || prev.selectedOrder,
      viewMode: 'job',
      lastAction: { type: 'select_job', jobId, orderId, timestamp: Date.now() }
    }));

    // Load detailed results if not already loaded
    if (!resultsData.detailedResults[jobId]) {
      resultsData.loadDetailedResults(jobId);
    }

    if (enablePerformanceMonitoring) {
      performance.trackModalAction('job_selected', { jobId, orderId });
    }
  }, [resultsData, performance, enablePerformanceMonitoring]);

  const setViewMode = useCallback((mode) => {
    setState(prev => ({
      ...prev,
      viewMode: mode,
      lastAction: { type: 'view_mode_change', mode, timestamp: Date.now() }
    }));
  }, []);

  // Data loading actions
  const loadMore = useCallback(async () => {
    if (enablePerformanceMonitoring) {
      return await performance.trackAsyncOperation('load_more_orders', 
        ordersData.loadMore()
      );
    } else {
      return await ordersData.loadMore();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ordersData.loadMore, performance, enablePerformanceMonitoring]); // ordersData intentionally excluded to prevent infinite loops

  const refresh = useCallback(async () => {
    setState(prev => ({
      ...prev,
      expandedOrders: {},
      selectedOrder: null,
      selectedJob: null,
      lastAction: { type: 'refresh', timestamp: Date.now() }
    }));

    if (enablePerformanceMonitoring) {
      return await performance.trackAsyncOperation('refresh_data', 
        ordersData.refresh()
      );
    } else {
      return await ordersData.refresh();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ordersData.refresh, performance, enablePerformanceMonitoring]); // ordersData intentionally excluded to prevent infinite loops

  // Search functionality
  const searchAll = useCallback((searchTerm) => {
    const results = {
      orders: ordersData.orders.filter(order => 
        order.orderName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.description?.toLowerCase().includes(searchTerm.toLowerCase())
      ),
      jobs: jobsData.jobs.filter(job =>
        job.jobName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        job.provider?.toLowerCase().includes(searchTerm.toLowerCase())
      ),
      content: resultsData.searchContent(searchTerm)
    };

    if (enableAccessibility) {
      const totalResults = results.orders.length + results.jobs.length + results.content.length;
      accessibility.announce(
        `Search found ${totalResults} result${totalResults === 1 ? '' : 's'}`,
        'polite'
      );
    }

    return results;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ordersData.orders, jobsData.jobs, resultsData.searchContent, accessibility, enableAccessibility]); // resultsData intentionally excluded to prevent infinite loops

  // Effect for data loaded callback
  useEffect(() => {
    if (onDataLoaded && enhancedOrders.length > 0) {
      onDataLoaded({
        orders: enhancedOrders,
        stats: unifiedStats,
        timestamp: Date.now()
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enhancedOrders.length, onDataLoaded, unifiedStats]); // enhancedOrders intentionally excluded - only tracking length changes

  // Effect for error callback
  useEffect(() => {
    if (onError && error) {
      onError(error);
    }
  }, [error, onError]);

  // Initial data load trigger with circuit breaker
  // Note: loadOrders is intentionally NOT in dependencies to prevent infinite loops
  const hasEverLoadedRef = useRef(false);
  const loadAttemptCountRef = useRef(0);
  const lastLoadTimeRef = useRef(0);
  
  useEffect(() => {
    // If we've already successfully loaded data, don't load again
    // This prevents StrictMode remounts from triggering duplicate loads
    if (hasEverLoadedRef.current) {
      console.log('🔄 useOrdersWithJobsData: Skipping load - already loaded');
      return;
    }
    
    const now = Date.now();
    const timeSinceLastLoad = now - lastLoadTimeRef.current;
    
    // Circuit breaker: prevent more than 3 loads within 5 seconds
    if (timeSinceLastLoad < 5000 && loadAttemptCountRef.current >= 3) {
      console.error('🚨 Circuit breaker activated - too many load attempts', {
        attempts: loadAttemptCountRef.current,
        timeSinceLastLoad
      });
      return;
    }
    
    // Reset counter if enough time has passed
    if (timeSinceLastLoad >= 5000) {
      loadAttemptCountRef.current = 0;
    }
    
    if (ordersData.isReady && !ordersData.loading && ordersData.orders.length === 0) {
      // Only trigger initial load if we're ready, not currently loading, and have no data
      console.log('🔄 useOrdersWithJobsData: Triggering initial load', {
        attempt: loadAttemptCountRef.current + 1
      });
      loadAttemptCountRef.current++;
      lastLoadTimeRef.current = now;
      ordersData.loadOrders(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ordersData.isReady, ordersData.loading, ordersData.orders.length]);
  
  // Mark as loaded when we have data
  useEffect(() => {
    if (ordersData.orders.length > 0 || (!ordersData.loading && ordersData.isReady)) {
      hasEverLoadedRef.current = true;
    }
  }, [ordersData.orders.length, ordersData.loading, ordersData.isReady]);

  // Get current selection data
  const getCurrentSelection = useCallback(() => {
    const order = state.selectedOrder ? 
      ordersData.getOrderById(state.selectedOrder) : null;
    const job = state.selectedJob ? 
      jobsData.getJobById(state.selectedJob) : null;
    const results = state.selectedJob ? 
      resultsData.getResultsForJob(state.selectedJob) : null;

    return { order, job, results };
  }, [state.selectedOrder, state.selectedJob, ordersData, jobsData, resultsData]);

  return {
    // Enhanced data
    orders: enhancedOrders,
    jobs: jobsData.jobsWithResults,
    results: resultsData.pooledResults,
    stats: unifiedStats,
    
    // Raw data access
    rawData: {
      orders: ordersData,
      jobs: jobsData,
      results: resultsData
    },
    
    // State
    loading: isLoading,
    error,
    viewMode: state.viewMode,
    selectedOrder: state.selectedOrder,
    selectedJob: state.selectedJob,
    expandedOrders: state.expandedOrders,
    lastAction: state.lastAction,
    
    // Actions
    toggleOrderExpansion,
    selectOrder,
    selectJob,
    setViewMode,
    loadMore,
    refresh,
    searchAll,
    
    // Utilities
    getCurrentSelection,
    hasMore: ordersData.hasMore,
    isReady: ordersData.isReady && jobsData.isReady,
    
    // Optional features
    ...(enableAccessibility && { accessibility }),
    ...(enablePerformanceMonitoring && { performance })
  };
};
